// ink-component
import React, { ReactNode, ButtonHTMLAttributes } from 'react';
import styled, { ThemeProvider } from 'styled-components';

import getGlobalProps from '../../helpers/constants/global-props';
import useInkAnalytics from '../../hooks/useInkAnalytics';

import Icon from '../Icon/Icon';

import { buttonBase, buttonVariants, buttonSizes } from './styles';
import { renderLinkStyles } from '../Anchor/Anchor';

/*
  Notes:

  1. We should codemod `type` into `variant` because type is a default attribute
  on the HTML button element. `Operators` should also get rolled in to `variant`
  since they cannot be used in conjunction with other variants or sizes
  anyways. It'll make our API easier to use.

  2. `variant` is a stylistic modifier while `size` is a sizing modifier making
  it more modular to mix and match. The exceptions are `link`, `plus`, `minus`,
  and `actions` as it is a style variant that overrides size as well.

  3. `inverse` does not follow other variant styling patterns

  4. `active` was only migrated with `actions`, `default`, `primary`, `danger`,
  `inverse` since the others have no visible :active effect to preserve on an
  `activeTrigger`

  5. We should rename `aura` for context clarity (explicit intent)

  6. We should rename `link` to `anchor`

  7. Once we decouple the conditional anchor from button, we should remove
  these props since they are used for Anchors not Buttons
    - `href`
    - `target`
    - `rel`
    - `wrapText`

*/

// We can stop omitting type once we fix the API!
export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  /** Unique identifier */
  id?: string;
  /** Contents the will be wrapped in the tag */
  children?: ReactNode;
  /** Callback for the click event */
  onClick?: (event?: any) => void;
  /** Html href */
  href?: string;
  /** Html target */
  target?: string;
  /** Html rel */
  rel?: string;
  /** Button size modifier */
  size?: 'auto' | 'mini' | 'small' | 'wide' | 'full';
  /** Changes the Button visuals, like background and text color.
   * `aura` makes it virtually invisible, `link` emulates an `<a>` element,
   * `actions` **replace** content with arrow menu 🔽 and `inverse` should
   * only be used when it sits against an inverse background.
   * **DON'T** use `black` or `underline` yet! */
  type?:
    | 'aura'
    | 'link'
    | 'danger'
    | 'actions'
    | 'primary'
    | 'inverse'
    | 'transparent'
    | 'default'
    | 'black'
    | 'underline'
    | 'filter';
  /** The HTML button tag "type" attribute, as defined [here](https://developer.mozilla.org/en-US/docs/Web/API/HTMLButtonElement) */
  htmlType?: 'button' | 'submit' | 'reset';
  /** Operators will **replace** content with + or - signs. */
  operators?: 'plus' | 'minus';
  /** Cell modifier for tables, overwrites size modifier. */
  isCell?: boolean;
  /** Disabled state modifier */
  isDisabled?: boolean;
  /** Disables the button while async process are running */
  isLoading?: boolean;
  /** Wraps the text for longer text when the type is 'link' */
  wrapText?: boolean;
  /** React ref object to be forwarded to the internal node */
  externalRef?: React.RefObject<HTMLButtonElement | HTMLAnchorElement | null>;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
  /** Allows screen readers to describe the content */
  'aria-label'?: string;
  /** Unique name for analytics purposes. By default, components are not tracked, unless this is set */
  'data-track-name'?: string;
  /** Custom payload to be sent to analytics provider */
  'data-track-payload'?: any;
  /** List of tracked events in the analytics integration */
  'data-track-events'?: string[];
  /** @ignore */
  activeTrigger?: boolean;
}

/*
  Styling notes:
  - `buttonBase` - Normalizes button across browsers
  - `buttonSizes` - Refers to the `size` API with the inclusion of `isCell` (as
    `cell`); This has to be defined before type because certain types don't
    have a clear separation between style and size. (See note #2)
  - `buttonVariants` - This refers to prop `type` including the `operators`
    props (see note #1)
*/

/* @internal */
export const StyledButton = styled.button`
  ${buttonBase};
  ${({ theme }) => buttonSizes[theme.size]};
  ${({ theme }) =>
    ['link', 'black', 'underline'].includes(theme.type)
      ? renderLinkStyles(theme.type, theme.tileVariant)
      : buttonVariants[theme.type]}
`;

// This is temporary for the Sass > Styled migration parity. This should be deprecated when we detach the conditional anchor rendering from Button.
const LegacyAnchor = styled.a`
  ${buttonBase};
  ${({ theme }) => buttonSizes[theme.size]};
  ${({ theme }) => buttonVariants[theme.type]};
`;

const globalProps = getGlobalProps({});

/**
 * Description
 * @purpose Triggers actions through onClick
 * @typescript true
 * @categories navigation
 */
export function Button(props: ButtonProps) {
  const {
    activeTrigger,
    children,
    className,
    href,
    htmlType,
    id,
    isCell,
    isDisabled,
    isLoading,
    onClick,
    operators,
    rel,
    size,
    style,
    target,
    type,
    wrapText,
    externalRef,
    'data-testid': dataTestId,
    'aria-label': ariaLabel,
    ...otherProps
  } = props;

  const [ref] = useInkAnalytics(props, { ref: externalRef });

  const defineChildren = () => {
    if (type === 'actions') {
      return <Icon type="more-vertical" meaning={ariaLabel || 'Open menu'} />;
    }
    if (operators) {
      const iconType = operators === 'plus' ? 'add' : 'minus';
      const meaning = ariaLabel || iconType;
      return <Icon type={iconType} size="md" meaning={meaning} />;
    }

    return children;
  };

  // inverse of the logic in defineChildren (🤮)
  const label = !(type === 'actions' || operators) ? ariaLabel : undefined;

  return (
    <ThemeProvider
      theme={currentTheme => ({
        ...currentTheme,
        active: activeTrigger,
        disabled: isDisabled || isLoading,
        size: isCell ? 'cell' : size,
        type: operators || type,
        wrapText: (type === 'link' || type === 'black') && wrapText,
      })}
    >
      {props.href ? (
        /*
          This should be DEPRECATED when we detach the conditional anchor
          rendering from Button. Only a link will render `href`, `rel` and
          `target`. This is temporary from the Sass > Styled migration parity.
          Link will not spread props!
        */
        <LegacyAnchor
          ref={ref}
          children={defineChildren()}
          data-testid={dataTestId}
          href={href}
          id={id}
          onClick={onClick}
          rel={rel}
          target={target}
          tabIndex={isDisabled || isLoading ? -1 : 0}
          aria-disabled={isDisabled || isLoading} // https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-disabled
          aria-label={label}
          {...globalProps}
        />
      ) : (
        <StyledButton
          ref={ref}
          children={defineChildren()}
          data-testid={dataTestId}
          disabled={isDisabled || isLoading}
          aria-disabled={isDisabled || isLoading}
          id={id}
          onClick={onClick}
          type={htmlType} // This must be explicit until we rename `type`
          aria-label={label}
          {...globalProps}
          {...otherProps}
        />
      )}
    </ThemeProvider>
  );
}

Button.defaultProps = {
  children: undefined,
  href: undefined,
  htmlType: 'button',
  id: undefined,
  isCell: false,
  isDisabled: false,
  isLoading: false,
  onClick: () => {},
  operators: undefined,
  rel: undefined,
  size: 'auto',
  target: undefined,
  type: 'default',
  wrapText: false,
  'data-testid': undefined,
  'data-track-events': ['click'],
};

Button.displayName = 'Button';

export default Button;
